import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface AmendTarget {
  id: number;
  who: string;
  what: string;
  claimed: number;
}

interface AmendDialogProps {
  target: AmendTarget | null;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (amount: number, remarks: string) => void;
}

interface AmendFormProps {
  target: AmendTarget;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (amount: number, remarks: string) => void;
}

function AmendForm({ target, isPending, onClose, onSubmit }: AmendFormProps) {
  const [amount, setAmount] = useState(String(target.claimed));
  const [remarks, setRemarks] = useState('');

  const parsed = Number(amount);
  const invalid = amount.trim() === '' || Number.isNaN(parsed) || parsed < 0;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="amend-amount">Corrected amount (Tk)</Label>
          <Input
            id="amend-amount"
            inputMode="decimal"
            value={amount}
            aria-invalid={invalid}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="amend-remarks">Why</Label>
          <Textarea
            id="amend-remarks"
            rows={3}
            value={remarks}
            placeholder="What the ZM sent in, or what was wrong"
            onChange={(event) => setRemarks(event.target.value)}
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={invalid || isPending}
          onClick={() => onSubmit(parsed, remarks)}
        >
          {isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Saving
            </>
          ) : (
            'Save correction'
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

export function AmendDialog({ target, isPending, onClose, onSubmit }: AmendDialogProps) {
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Correct the claim</DialogTitle>
          <DialogDescription>
            {target ? `${target.who} - ${target.what}, claimed ${target.claimed} Tk.` : ''}
          </DialogDescription>
        </DialogHeader>

        {target ? (
          <AmendForm
            key={target.id}
            target={target}
            isPending={isPending}
            onClose={onClose}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
